import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { getRestaurantsOrHotels } from "../../api/user";
import { getAvailableRooms } from "../../api/user/hotels";
import { useTranslation } from "../../hooks/useTranslation";
import Pagination from "../../components/common/Pagination";

function haversineKm(lat1, lon1, lat2, lon2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371; // km
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

const DummyQR = ({ label = "QR" }) => (
  <div className="w-full h-full min-h-[150px] flex items-center justify-center border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
    <span className="text-gray-400 text-sm font-medium">{label}</span>
  </div>
);

export default function NearbyHotels() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [coords, setCoords] = useState(null);
  const [selectedHotelId, setSelectedHotelId] = useState(null);

  useEffect(() => {
    if (!("geolocation" in navigator)) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoords({
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
        });
      },
      () => {
        // If denied, stay on page and show all results fallback later
        setCoords(null);
      },
      { enableHighAccuracy: true, maximumAge: 10000, timeout: 10000 }
    );
  }, []);

  const { data: listingsData, isLoading: listingsLoading } = useQuery({
    queryKey: ["nearby-hotels", coords?.latitude, coords?.longitude],
    queryFn: () =>
      getRestaurantsOrHotels({
        hotels: true,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        limit: 100,
      }),
    enabled: true,
    keepPreviousData: true,
  });

  const hotels = listingsData?.data?.data || [];

  const nearbyHotels = useMemo(() => {
    if (!coords) return hotels; // if no location, show all as fallback
    return hotels.filter((h) => {
      const lat = h?.location?.coordinates?.[1];
      const lon = h?.location?.coordinates?.[0];
      if (typeof lat !== "number" || typeof lon !== "number") return false;
      const distKm = haversineKm(coords.latitude, coords.longitude, lat, lon);
      // return distKm <= 0.1; // 100 meters
      return distKm <= 1000; // 100 meters
    });
  }, [coords, hotels]);

  const [roomPage, setRoomPage] = useState(1);
  const roomLimit = 6;

  // Reset pagination when hotel selection changes
  useEffect(() => {
    setRoomPage(1);
  }, [selectedHotelId]);

  const { data: roomsData, isFetching: roomsLoading } = useQuery({
    queryKey: ["hotel-rooms", selectedHotelId, roomPage, roomLimit],
    queryFn: () =>
      getAvailableRooms({
        hotelId: selectedHotelId,
        page: roomPage,
        limit: roomLimit,
      }),
    enabled: !!selectedHotelId,
    keepPreviousData: true,
  });

  const rooms = roomsData?.data?.rooms || roomsData?.data?.data?.rooms || [];
  const roomsPagination = roomsData?.data?.pagination || roomsData?.data?.data?.pagination;

  // Get QR codes from rooms (only real rooms, no dummy placeholders)
  const qrCodes = useMemo(() => {
    if (!rooms || rooms.length === 0) return [];
    return rooms.map((room) => ({
      id: room._id,
      qrCodeUrl: room.qrCodeUrl,
      roomNumber: room.roomNumber,
      roomName: `${t("home")?.hotelBooking?.room || "Room"} ${room.roomNumber}`,
      capacity: room.roomCapacity,
      bedType: room.bedType,
    }));
  }, [rooms, t]);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            {t("home")?.hotelBooking?.nearbyTitle || "Nearby Hotels"}
          </h1>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate("/user-orders" )}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-[#4CAF50] hover:bg-[#209b25] rounded-lg transition-colors shadow-sm"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              {t("home")?.hotelBooking?.myOrders || "My Orders"}
            </button>
            <button
              onClick={() => navigate(-1)}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-[#4CAF50] hover:text-[#209b25] transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
              </svg>
              {t("common")?.back || "Back"}
            </button>
          </div>
        </div>

        {/* Loading State */}
        {listingsLoading && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4CAF50]"></div>
          </div>
        )}

        {/* Empty State */}
        {!listingsLoading && nearbyHotels.length === 0 && (
          <div className="bg-white rounded-xl shadow-sm p-8 text-center">
            <svg className="w-16 h-16 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <p className="text-gray-700 text-lg">
              {t("home")?.hotelBooking?.noNearbyHotel ||
                "No hotel found nearby."}
            </p>
          </div>
        )}

        {/* Main Content: 1:2 Split (Hotel listing is half width of QR codes) */}
        {!listingsLoading && nearbyHotels.length > 0 && (
          <div className="grid lg:grid-cols-[1fr_2fr] gap-6">
            {/* Left Side: Hotel Listing (1/3 width - half of QR codes section) */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
                {t("home")?.hotelBooking?.selectHotelTitle || "Select Hotel"}
              </h2>
              <div className="space-y-3 max-h-[calc(100vh-300px)] overflow-y-auto pr-2">
                {nearbyHotels.map((h) => (
                  <button
                    key={h?._id}
                    onClick={() => setSelectedHotelId(h?._id)}
                    className={`w-full text-left p-4 rounded-lg border-2 transition-all duration-200 ${
                      selectedHotelId === h?._id
                        ? "border-[#4CAF50] bg-green-50 shadow-md"
                        : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 mb-1">{h?.name}</p>
                        <p className="text-sm text-gray-500 flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          {h?.address || t("home")?.hotelBooking?.addressNotAvailable || "Address not available"}
                        </p>
                      </div>
                      {selectedHotelId === h?._id && (
                        <div className="ml-3">
                          <svg className="w-6 h-6 text-[#4CAF50]" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Right Side: QR Codes Section (2/3 width - double the hotel listing) */}
            <div className="bg-white rounded-xl shadow-sm p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 pb-3 border-b border-gray-200">
                {t("home")?.hotelBooking?.roomQRCodes || "Room QR Codes"}
              </h2>

              {!selectedHotelId && (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  <p className="text-gray-500 text-lg">
                    {t("home")?.hotelBooking?.selectHotel || "Select a hotel to view QR codes"}
                  </p>
                </div>
              )}

              {selectedHotelId && roomsLoading && (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#4CAF50]"></div>
                </div>
              )}

              {selectedHotelId && !roomsLoading && (
                <>
                  {/* QR Codes Grid - Show 3 per row, at least 6 total */}
                  <div className="grid grid-cols-3 gap-4 mb-6">
                    {qrCodes.map((qr) => (
                      <div
                        key={qr.id}
                        className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                      >
                        <div className="mb-3 flex justify-center">
                          {qr.qrCodeUrl ? (
                            <img
                              src={qr.qrCodeUrl}
                              alt={`QR Code - ${qr.roomName}`}
                              className="w-full h-auto max-h-[150px] object-contain"
                              onError={(e) => {
                                e.currentTarget.style.display = "none";
                                const dummyDiv = e.currentTarget.parentElement.querySelector(".dummy-qr");
                                if (dummyDiv) dummyDiv.style.display = "flex";
                              }}
                            />
                          ) : (
                            <div className="dummy-qr w-full min-h-[150px]">
                              <DummyQR label={qr.roomName} />
                            </div>
                          )}
                        </div>
                        <div className="text-center">
                          <p className="font-semibold text-sm text-gray-900 mb-1">{qr.roomName}</p>
                          {qr.capacity && (
                            <p className="text-xs text-gray-500 mb-1">
                              {t("allRooms")?.tableHeaders?.capacity || t("hotelDetail")?.capacity || "Capacity"}: {qr.capacity}
                            </p>
                          )}
                          {qr.bedType && (
                            <p className="text-xs text-gray-500 mb-2">
                              {t("allRooms")?.tableHeaders?.bedType || t("hotelDetail")?.bedType || "Bed"}: {qr.bedType}
                            </p>
                          )}
                          {/* <button
                            className="text-xs text-[#4CAF50] hover:text-[#209b25] font-medium underline"
                            onClick={() =>
                              navigate(
                                `/hotel-menu?hotelId=${selectedHotelId}&roomId=${qr.id}&guest=true`
                              )
                            }
                          >
                            {t("home")?.hotelBooking?.orderFromRoom || "Order from this room"}
                          </button> */}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {roomsPagination && roomsPagination.totalPages > 1 && (
                    <div className="border-t border-gray-200 pt-4">
                      <Pagination
                        page={roomsPagination.page || roomPage}
                        totalPages={roomsPagination.totalPages}
                        setPage={setRoomPage}
                      />
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


